import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { ROUTES } from './config';
import AppShell from './components/AppShell.jsx';
import RequireAuth from './components/RequireAuth.jsx';
import PublicHostOnly from './components/PublicHostOnly.jsx';
import RequirePermission from './components/RequirePermission.jsx';
// ``LandingRedirect`` is no longer referenced here — it moved into
// ``pages/marketing/RootRoute.jsx``, which owns the ``/`` decision.


import LoginPage from './pages/LoginPage.jsx';
import SignupPage from './pages/SignupPage.jsx';
import PasswordRecoveryPage from './pages/PasswordRecoveryPage.jsx';
import AccessDenied from './pages/errors/AccessDenied.jsx';
import SSOLogin from './pages/sso/SSOLogin.jsx';
import ProfilePage from './pages/ProfilePage.jsx';

// Lazy-loaded marketing chunks (workspace finder removed — login/signup at /login).
const MarketingLayout = React.lazy(() => import('./pages/marketing/MarketingLayout.jsx'));
const RootRoute = React.lazy(() => import('./pages/marketing/RootRoute.jsx'));
const MarketingFeatures = React.lazy(() => import('./pages/marketing/FeaturesPage.jsx'));
const MarketingSolutions = React.lazy(() => import('./pages/marketing/SolutionsPage.jsx'));
const MarketingHelp = React.lazy(() => import('./pages/marketing/HelpPage.jsx'));
const MarketingPrivacy = React.lazy(() => import('./pages/marketing/PrivacyPage.jsx'));

/**
 * Suspense fallback for the lazy marketing chunks.
 *
 * Reuses the app's existing ``ty-fullscreen-center`` / ``ty-spinner``
 * pair (same markup ``RequireAuth`` shows while bootstrapping) so the
 * brief loading frame matches the rest of the product instead of being
 * a blank white page.
 */
function PublicFallback() {
  return (
    <div className="ty-fullscreen-center">
      <div className="ty-spinner" aria-label="Loading" />
    </div>
  );
}




import TasksDashboard from './pages/tasks/TasksDashboard.jsx';
import TasksList from './pages/tasks/TasksList.jsx';
import TaskDetails from './pages/tasks/TaskDetails.jsx';
import TaskCreate from './pages/tasks/TaskCreate.jsx';
import TaskEdit from './pages/tasks/TaskEdit.jsx';
import RoutineTasksList from './pages/tasks/RoutineTasksList.jsx';
import RoutineTaskCreate from './pages/tasks/RoutineTaskCreate.jsx';
import RoutineTaskEdit from './pages/tasks/RoutineTaskEdit.jsx';
import RoutineTaskImport from './pages/tasks/RoutineTaskImport.jsx';


import ComplaintsDashboard from './pages/complaints/ComplaintsDashboard.jsx';
import ComplaintsList from './pages/complaints/ComplaintsList.jsx';
import ComplaintDetails from './pages/complaints/ComplaintDetails.jsx';
import ComplaintCreate from './pages/complaints/ComplaintCreate.jsx';
import ComplaintEdit from './pages/complaints/ComplaintEdit.jsx';

import ChatPage from './pages/chat/ChatPage.jsx';

import BusinessHome from './pages/business/BusinessHome.jsx';
import BusinessDefaults from './pages/business/BusinessDefaults.jsx';
import BusinessAccessControl from './pages/business/BusinessAccessControl.jsx';
import BusinessGoogleSheets from './pages/business/BusinessGoogleSheets.jsx';
import BusinessPublicComplaints from './pages/business/BusinessPublicComplaints.jsx';
import BusinessComplaintSettings from './pages/business/BusinessComplaintSettings.jsx';
import BusinessTimezoneSettings from './pages/business/BusinessTimezoneSettings.jsx';
import BusinessSchemaBackup from './pages/business/BusinessSchemaBackup.jsx';
import BusinessAppBranding from './pages/business/BusinessAppBranding.jsx';

import {
  BusinessResourceList,
  BusinessResourceCreate,
  BusinessResourceEdit,
} from './pages/business/BusinessResource.jsx';


import SuperAdminHome from './pages/super_admin/SuperAdminHome.jsx';
import LoginSecurityConfig from './pages/super_admin/LoginSecurityConfig.jsx';
import DatabaseViewer from './pages/super_admin/DatabaseViewer.jsx';
import ModuleDefaults from './pages/super_admin/ModuleDefaults.jsx';
import SuccessYatraMove from './pages/super_admin/SuccessYatraMove.jsx';
import OrganisationModules from './pages/super_admin/OrganisationModules.jsx';
import {
  SuperAdminResourceList,
  SuperAdminResourceCreate,
  SuperAdminResourceEdit,
} from './pages/super_admin/SuperAdminResource.jsx';

import HandoffPage from '@taskydo/web-core/HandoffPage.jsx';
import ProductHandoffRedirect from '@taskydo/web-core/ProductHandoffRedirect.jsx';

// Uni Email / SMS / Forms — engagement modules ported from an
// external project, rewritten for the SPA.  Each module exports a
// dashboard + a handful of read-mostly list pages backed by a DRF
// DefaultRouter (see ``uni_email/api_urls.py``, ``uni_sms/api_urls.py``,
// ``uni_forms/api_urls.py``).  Tenant-only — the public URLConf
// doesn't mount the ``/api/uni-*/`` API.
import {
  UniEmailDashboard, UniEmailContacts, UniEmailLists,
  UniEmailTemplates, UniEmailCampaigns,
} from './pages/uni/UniEmailPages.jsx';
import {
  UniSmsDashboard, UniSmsContacts, UniSmsLists,
  UniSmsDLTTemplates, UniSmsCampaigns,
} from './pages/uni/UniSmsPages.jsx';
import {
  UniFormsDashboard, UniFormsForms, UniFormsForMe, UniFormsMySubmissions,
  UniFormsQuizzes,
  UniFormsPolls, UniFormsTemplates,
} from './pages/uni/UniFormsPages.jsx';
// Administrative Uni Forms surfaces — audience/visibility, figures
// pulled from other modules, and the admin report views.  Kept in
// their own module so the builder pages stay lean.
import {
  UniFormsAudience, UniFormsFigures,
  UniFormsReportTemplates,
} from './pages/uni/UniFormsAdminPages.jsx';
import { UniFormsReports } from './pages/uni/UniFormsReportsPage.jsx';
import {
  UniFormsFormResponses, UniFormsPollResults, UniFormsQuizResponses,
} from './pages/uni/UniFormsResponsePages.jsx';
import { UniFormsQuestions } from './pages/uni/UniFormsQuestionPages.jsx';

import { WabaDashboard, WabaContacts, WabaLists, WabaTags, WabaLabels,
  WabaBroadcasts, WabaTemplates, WabaMedia, WabaAnalytics, WabaWebhooks,
  WabaIncomingLogs, WabaSettings, WabaNumbers, WabaS3, WabaRetry, WabaThrottle,
  WabaFailed, WabaAutomation, WabaApiKeys, WabaQuickReplies, WabaAutoReplies,
  WabaTeams, WabaFormDialogs, WabaFormSessions, WabaKycSessions, WabaNotes,
  WabaHealth, WabaHooks, WabaHelp, WabaFeatures,
} from './pages/waba/WabaPages.jsx';
import WabaInbox from './pages/waba/WabaInbox.jsx';
import { WabaFlows, WabaFlowBuilder } from './pages/waba/WabaFlows.jsx';
import {
  RagDashboard, RagKnowledge, RagCategories, RagVersions, RagChunks,
  RagTraining, RagQuality, RagFaq, RagChats, RagConversations, RagCustomers,
  RagSales, RagEscalation, RagFollowups, RagSequences, RagSequenceSteps,
  RagTrackers, RagReports, RagHealthReports, RagProducts, RagPlayground,
  RagSystems, RagConfig, RagConfigKeys, RagCosts, RagAudit, RagAlerts, RagCompliance,
  RagResponseTemplates, RagDocumentTemplates, RagFeedback, RagNotes,
  RagShopify, RagTeam, RagHooks, RagHelp, RagFeatures, RagAutomation,
} from './pages/rag/RagPages.jsx';
import RagTestChat from './pages/rag/RagTestChat.jsx';
import {
  SignalBridgeDashboard, SignalBridgePixels, SignalBridgeStores, SignalBridgeParameters,
  SignalBridgeTestEvent, SignalBridgeResend, SignalBridgeUpload, SignalBridgeBatchDetail,
  SignalBridgeEvents, SignalBridgeEventDetail, SignalBridgePayloads, SignalBridgeHelp,
} from './pages/signalbridge/SignalBridgePages.jsx';
import { SignalBridgeBuyerReport } from './pages/signalbridge/BuyerInsights.jsx';
import { SignalBridgeTemplates } from './pages/signalbridge/SignalBridgeMiddleware.jsx';
import {
  SignalBridgeDestinations, SignalBridgeRoutes, SignalBridgeCollect, SignalBridgeApiKeys,
  SignalBridgeAlerts, SignalBridgeReports, SignalBridgeAudit, SignalBridgeOnboarding,
  SignalBridgeSettings, SignalBridgeConnHealth,
} from './pages/signalbridge/SignalBridgePlatform.jsx';
import {
  SignalBridgePixelGroups, SignalBridgeGoogleWizard, SignalBridgeJourney,
  SignalBridgeDlq, SignalBridgeCompare,
} from './pages/signalbridge/SignalBridgeMore.jsx';
import {
  SignalBridgePixelWizard, SignalBridgeStoreWizard, SignalBridgeEventsWizard,
  SignalBridgeMergeWizard, SignalBridgeMergeBrowser,
} from './pages/signalbridge/SignalBridgeWizards.jsx';
// The PUBLIC form-fill page.  Lazy-loaded and mounted OUTSIDE the
// authenticated shell (see the route block below): it is what a published
// share link opens, so it must render for a visitor with no account and
// should not drag the authenticated bundle in with it.
const UniFormsPublicFill = React.lazy(
  () => import('./pages/uni/UniFormsPublicFill.jsx'),
);
const UniFormsReportTemplateWizard = React.lazy(
  () => import('./pages/uni/UniFormsReportTemplateWizard.jsx'),
);
const UniFormsSharedReport = React.lazy(
  () => import('./pages/uni/UniFormsSharedReport.jsx'),
);




/**
 * Top-level router.
 *
 * Layout:
 *   /login                           - public
 *   /password-recovery               - public (request + emailed confirm)
 *   /access-denied                   - shared friendly error page
 *                                      (rendered for permission denials,
 *                                       API 403s, and unknown routes;
 *                                       see ``AccessDenied`` for the
 *                                       supported ``?reason=`` values)
 *   /                                - permission-aware landing redirect
 *                                      (super-admin -> /super-admin,
 *                                       org user    -> first permitted app)
 *   /task/...                        - Tasks       (permission-gated)
 *   /complaints/...                  - Complaints  (permission-gated)
 *   /chat[/:conversationId]          - Chat        (permission-gated)
 *   /business/...                    - Org Admin   (org-admin-gated)
 *   /super-admin/...                 - Super Admin (super-admin-gated)
 *   *                                - any other path -> "page not found"
 *
 * Two layers of protection wrap every authenticated route:
 *
 *   1. ``RequireAuth``        — bounces unauthenticated users to /login.
 *   2. ``RequirePermission``  — checks ``user.permitted_apps`` (from
 *                              ``/api/super_admin/me/``) and renders the
 *                              friendly ``AccessDenied`` page with the
 *                              right reason code if the current user
 *                              can't use the app.  Crucially, it does
 *                              this BEFORE the page would call its
 *                              tenant-only API — so a super-admin on
 *                              ``tasky.do`` clicking "Tasks" never fires
 *                              the ``GET /api/task/tasks/summary/`` call
 *                              that produced the 404 storm in the brief.
 *
 * The bare ``/`` no longer hard-codes a redirect to ``/task`` —
 * ``LandingRedirect`` picks the right destination per user type +
 * host.
 *
 * The catch-all ``*`` route is now a real "page not found" screen
 * (``AccessDenied`` with reason="not-found") rather than a silent
 * redirect, so users who follow a stale link see *why* their click
 * went nowhere.
 *
 * Security note: every guard here is UX gating only.  The backend
 * still rejects unauthorised API calls via per-app DRF permissions,
 * ``TenantAwareJWTAuthentication`` (schema-claim check) and
 * ``TenantUserBindingMiddleware`` (org-id check).
 */
export default function App() {
  return (
    <Routes>
      <Route path={ROUTES.LOGIN}         element={<LoginPage />} />
      <Route path="/auth/handoff"        element={<HandoffPage />} />
      <Route
        path={ROUTES.SIGNUP}
        element={(
          <PublicHostOnly>
            <SignupPage />
          </PublicHostOnly>
        )}
      />
      <Route path={ROUTES.PASSWORD_RECOVERY} element={<PasswordRecoveryPage />} />

      {/* ``AccessDenied`` is mounted at the top level (no AppShell) so
          it renders even when something is wrong with the user/session
          context and we don't want the rail/topbar to fail with it.
          The page reads ``?reason=`` from the URL — see
          ``AccessDenied`` for the enum. */}
      <Route path={ROUTES.ACCESS_DENIED} element={<AccessDenied />} />

      {/* Auretics SSO landing — PUBLIC route mounted ABOVE the
          ``RequireAuth`` shell so an external user with a valid
          Auretics JWT in the ``?token=`` query param can land here
          without being bounced to ``/login``.  Matches both the
          trailing-slash and non-trailing-slash variants because the
          legacy server-rendered URL Auretics deep-links to
          (``/complaints/sso/login/``) included a trailing slash.
          The page itself swaps the Auretics token for a Taskydo JWT
          pair and ``navigate()``s into ``/complaints/create``.  See
          ``react_frontend/src/pages/sso/SSOLogin.jsx`` and
          ``complaints_management_system/views.api_sso_login``. */}
      <Route path={ROUTES.SSO_LOGIN}      element={<SSOLogin />} />
      <Route path={ROUTES.SSO_LOGIN + '/'} element={<SSOLogin />} />

      {/* ===== Uni Forms — the PUBLIC fill link ==========================
          Mounted ABOVE the ``RequireAuth`` shell, which is the entire
          point: a published form is meant to be filled by people who do
          not have a Taskydo account.

          This route is why the module's "Copy link" button ever worked.
          ``Form.share_url`` publishes ``/uni_forms/f/<slug>/`` and
          ``FormSerializer.public_url`` makes it absolute, so that URL is
          what users paste to respondents.  Nothing rendered it: Django
          mounted only an SPA shell (under the HYPHENATED ``uni-forms/``
          prefix), the legacy Django fill view can't even be imported in
          this deployment, and nginx sends every non-API path to
          ``index.html`` — where this router had no match and fell through
          to the catch-all ``*``, i.e. ``AccessDenied reason="not-found"``
          ("Page not found. The page you are looking for does not exist.").
          A perfectly valid published link was therefore guaranteed to
          404 for every recipient.

          BOTH spellings are registered, with and without the trailing
          slash, because the underscore form is already circulating in
          respondents' inboxes and must keep working forever:
            /uni_forms/f/:slug   ← what share_url publishes
            /uni-forms/f/:slug   ← canonical SPA spelling
          React Router 7 does not treat "/x" and "/x/" as the same path,
          so the explicit twins are load-bearing, not redundant.

          Declared BEFORE the authenticated ``/uni-forms/*`` builder
          routes so ``f/:slug`` can never be swallowed by them.

          ``Suspense`` is required because the page is a ``React.lazy``
          chunk; without a boundary the first render throws. */}
      {[
        ROUTES.UNI_FORMS_PUBLIC_FILL(),
        ROUTES.UNI_FORMS_PUBLIC_FILL() + '/',
        ROUTES.UNI_FORMS_PUBLIC_FILL_LEGACY(),
        ROUTES.UNI_FORMS_PUBLIC_FILL_LEGACY() + '/',
      ].map((publicFormPath) => (
        <Route
          key={publicFormPath}
          path={publicFormPath}
          element={
            <React.Suspense fallback={<PublicFallback />}>
              <UniFormsPublicFill />
            </React.Suspense>
          }
        />
      ))}

      {[
        ROUTES.UNI_FORMS_SHARED_REPORT(),
        ROUTES.UNI_FORMS_SHARED_REPORT() + '/',
        ROUTES.UNI_FORMS_TOKEN_REPORT(),
        ROUTES.UNI_FORMS_TOKEN_REPORT() + '/',
      ].map((sharedReportPath) => (
        <Route
          key={sharedReportPath}
          path={sharedReportPath}
          element={
            <React.Suspense fallback={<PublicFallback />}>
              <UniFormsSharedReport />
            </React.Suspense>
          }
        />
      ))}



      {/* ===== PUBLIC marketing site =====================================
          Mounted ABOVE the ``RequireAuth`` shell, which is the whole
          point: an anonymous visitor must be able to read these pages,
          whereas anything inside that shell bounces to /login.

          ``/`` is shared with the app and therefore delegates to
          ``RootRoute``, which shows the landing page to anonymous
          visitors and defers to ``LandingRedirect`` for signed-in users.
          It is declared exactly ONCE — see RootRoute.jsx for why two
          competing "/" routes would be order-dependent and fragile.

          The Login / Sign up tab points at ``/login`` (credential form plus
          workspace signup on the apex domain).

          ``Suspense`` is required because these are ``React.lazy``
          chunks; without a boundary the first render throws. The
          fallback is the app's own spinner so the loading state looks
          native rather than blank. */}
      <Route
        path="/"
        element={
          <React.Suspense fallback={<PublicFallback />}>
            <RootRoute />
          </React.Suspense>
        }
      />
      <Route
        element={
          <PublicHostOnly>
            <React.Suspense fallback={<PublicFallback />}>
              <MarketingLayout />
            </React.Suspense>
          </PublicHostOnly>
        }
      >
        <Route path="/features"  element={<MarketingFeatures />} />
        <Route path="/solutions" element={<MarketingSolutions />} />
        <Route path="/help"      element={<MarketingHelp />} />
        <Route path="/privacy"    element={<MarketingPrivacy />} />
        <Route path="/privacy/"   element={<MarketingPrivacy />} />
        <Route path="/copyright"  element={<MarketingPrivacy />} />
        <Route path="/copyright/" element={<MarketingPrivacy />} />
      </Route>

      <Route

        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        {/* NOTE: ``/`` is no longer declared here.  It moved above the
            RequireAuth shell so anonymous visitors can see the public
            landing page; ``RootRoute`` still calls ``LandingRedirect``
            for authenticated users, so signed-in behaviour is
            unchanged.  Do not re-add a ``/`` route inside this shell —
            two routes with the same path make the winner depend on JSX
            order. */}


        {/* ===== Self-service profile — every authenticated user =====
            No ``RequirePermission`` gate: a user is always allowed to
            edit their OWN identity (name, contact, password, profile
            picture).  The backend endpoint
            (``UserViewSet.update_me`` in ``super_admin/api_views.py``)
            whitelists the safe fields, so privilege flags / org / role
            cannot be touched from this page even if the request body
            tries to. */}
        <Route path={ROUTES.PROFILE} element={<ProfilePage />} />


        {/* ===== Tasks (gated on the "task" permission) =====
            Routine-task routes share the same permission — they're
            just a different view onto the Tasks app.  Order matters:
            the static ``/task/routine-tasks*`` paths MUST come before
            the parametric ``TASK_DETAILS`` (``/task/task/:id``) and
            ``TASK_EDIT`` (``/task/edit-task/:id``) so React Router
            doesn't accidentally match ``new`` / ``:id/edit`` as a
            task id. */}
        <Route element={<RequirePermission permission="task" />}>
          <Route path={ROUTES.TASKS_HOME}          element={<TasksDashboard />} />
          <Route path={ROUTES.TASKS_LIST}          element={<TasksList />} />
          <Route path={ROUTES.ROUTINE_TASKS_LIST}  element={<RoutineTasksList />} />
          <Route path={ROUTES.ROUTINE_TASK_NEW}    element={<RoutineTaskCreate />} />
          {/* ``ROUTINE_TASK_IMPORT`` is a static path
              (``/task/routine-tasks/import``); it must come BEFORE
              the parametric ``ROUTINE_TASK_EDIT``
              (``/task/routine-tasks/:id/edit``) so React Router
              doesn't try to load an id named "import". */}
          <Route path={ROUTES.ROUTINE_TASK_IMPORT} element={<RoutineTaskImport />} />
          <Route path={ROUTES.ROUTINE_TASK_EDIT}   element={<RoutineTaskEdit />} />
          <Route path={ROUTES.TASK_DETAILS}        element={<TaskDetails />} />
          <Route path={ROUTES.TASK_CREATE}         element={<TaskCreate />} />
          <Route path={ROUTES.TASK_EDIT}           element={<TaskEdit />} />
        </Route>


        {/* ===== Complaints (gated on the "complaints" permission) ===== */}
        <Route element={<RequirePermission permission="complaints" />}>
          <Route path={ROUTES.COMPLAINTS_HOME}    element={<ComplaintsDashboard />} />
          <Route path={ROUTES.COMPLAINTS_LIST}    element={<ComplaintsList />} />
          <Route path={ROUTES.COMPLAINT_DETAILS}  element={<ComplaintDetails />} />
          <Route path={ROUTES.COMPLAINT_CREATE}   element={<ComplaintCreate />} />
          <Route path={ROUTES.COMPLAINT_EDIT}     element={<ComplaintEdit />} />
        </Route>

        {/* ===== Chat (gated on the "chat" permission) ===== */}
        <Route element={<RequirePermission permission="chat" />}>
          <Route path={ROUTES.CHAT_HOME}         element={<ChatPage />} />
          <Route path={ROUTES.CHAT_CONVERSATION} element={<ChatPage />} />
        </Route>

        {/* ===== Business / Organisation Admin (gated to org admins) =====
            Order matters: the static ``/business/defaults`` page must
            come BEFORE the parametric ``/business/:resource`` route so
            React Router doesn't try to look "defaults" up as a CRUD
            resource slug in ``BUSINESS_RESOURCES``. */}
        <Route element={<RequirePermission permission="business" />}>
          <Route path={ROUTES.BUSINESS_HOME}          element={<BusinessHome />} />
          {/* System-defaults inspector / seeder.  Backed by
              ``organisational_admin/api_defaults.py``.  See
              ``BusinessDefaults.jsx`` for the rationale on why each
              fixture group exists. */}
          <Route path="/business/defaults"            element={<BusinessDefaults />} />
          {/* Per-user, per-module access-level matrix (Admin / User /
              View only / None).  Static path — must precede the
              parametric ``/business/:resource`` below so "access-control"
              isn't looked up as a CRUD resource slug.  See
              ``BusinessAccessControl.jsx``. */}
          <Route path="/business/access-control"      element={<BusinessAccessControl />} />
          <Route path="/business/integrations/google-sheets" element={<BusinessGoogleSheets />} />
          <Route path="/business/public-complaints" element={<BusinessPublicComplaints />} />
          <Route path="/business/complaint-settings" element={<BusinessComplaintSettings />} />
          <Route path="/business/timezone" element={<BusinessTimezoneSettings />} />
          <Route path="/business/schema-backup" element={<BusinessSchemaBackup />} />
          <Route path="/business/app-branding" element={<BusinessAppBranding />} />
          <Route path={ROUTES.BUSINESS_RESOURCE_NEW}  element={<BusinessResourceCreate />} />

          <Route path={ROUTES.BUSINESS_RESOURCE_EDIT} element={<BusinessResourceEdit />} />
          <Route path={ROUTES.BUSINESS_RESOURCE}      element={<BusinessResourceList />} />
        </Route>


        {/* Old pharma URLs leave Taskydo already signed in on Curedia. */}
        <Route path="/pharma/*" element={<ProductHandoffRedirect target="curedia" />} />


        {/* ===== Uni Email (gated on the "uni-email" permission) =====
            Engagement module — bulk email, contacts, templates,
            campaigns.  Tenant-only: API mounted on
            ``urls_tenant.py`` so the public host returns a 404. */}
        <Route element={<RequirePermission permission="uni-email" />}>
          <Route path={ROUTES.UNI_EMAIL_HOME}      element={<UniEmailDashboard />} />
          <Route path={ROUTES.UNI_EMAIL_CONTACTS}  element={<UniEmailContacts />} />
          <Route path={ROUTES.UNI_EMAIL_LISTS}     element={<UniEmailLists />} />
          <Route path={ROUTES.UNI_EMAIL_TEMPLATES} element={<UniEmailTemplates />} />
          <Route path={ROUTES.UNI_EMAIL_CAMPAIGNS} element={<UniEmailCampaigns />} />
        </Route>

        {/* ===== Uni SMS (gated on the "uni-sms" permission) ===== */}
        <Route element={<RequirePermission permission="uni-sms" />}>
          <Route path={ROUTES.UNI_SMS_HOME}          element={<UniSmsDashboard />} />
          <Route path={ROUTES.UNI_SMS_CONTACTS}      element={<UniSmsContacts />} />
          <Route path={ROUTES.UNI_SMS_LISTS}         element={<UniSmsLists />} />
          <Route path={ROUTES.UNI_SMS_DLT_TEMPLATES} element={<UniSmsDLTTemplates />} />
          <Route path={ROUTES.UNI_SMS_CAMPAIGNS}     element={<UniSmsCampaigns />} />
        </Route>

        {/* ===== Uni Forms (gated on the "uni-forms" permission) ===== */}
        <Route element={<RequirePermission permission="uni-forms" />}>
          <Route path={ROUTES.UNI_FORMS_HOME}      element={<UniFormsDashboard />} />
          <Route path={ROUTES.UNI_FORMS_FORMS}     element={<UniFormsForms />} />
          {/* "For Me" — the respondent's side of the module: forms a
              colleague selected THIS user for under "Display this Form for
              selected Internal Users".  Only the module permission gates
              it, and that is enough: ``forms/for-me/`` scopes itself to
              ``request.user`` with no widening parameter, so there is no
              row here the caller wasn't already picked for.  Declared
              before the ``forms/:formId/...`` patterns for the same reason
              the other static paths are. */}
          <Route path={ROUTES.UNI_FORMS_FOR_ME}    element={<UniFormsForMe />} />
          {/* "My Submissions" — forms this user has actually filled.
              Always scoped to request.user; declared before
              forms/:formId so the static path cannot be captured as an
              id. */}
          <Route path={ROUTES.UNI_FORMS_MY_SUBMISSIONS} element={<UniFormsMySubmissions />} />
          <Route path={ROUTES.UNI_FORMS_QUIZZES}   element={<UniFormsQuizzes />} />
          <Route path={ROUTES.UNI_FORMS_POLLS}     element={<UniFormsPolls />} />
          <Route path={ROUTES.UNI_FORMS_TEMPLATES} element={<UniFormsTemplates />} />
          {/* Admin surfaces.  Only the module permission is checked
              here; each screen additionally reflects the server's
              ``can_manage`` / ``can_lock`` answers, and every write
              endpoint re-checks them, so a non-admin who guesses the
              URL sees the settings read-only rather than editing
              them. */}
          <Route path={ROUTES.UNI_FORMS_AUDIENCE}  element={<UniFormsAudience />} />
          <Route path={ROUTES.UNI_FORMS_FIGURES}   element={<UniFormsFigures />} />
          <Route path={ROUTES.UNI_FORMS_REPORTS}   element={<UniFormsReports />} />
          <Route path={ROUTES.UNI_FORMS_REPORT_TEMPLATE_NEW}
                 element={
                   <React.Suspense fallback={null}>
                     <UniFormsReportTemplateWizard />
                   </React.Suspense>
                 } />
          <Route path={ROUTES.UNI_FORMS_REPORT_TEMPLATE_EDIT()}
                 element={
                   <React.Suspense fallback={null}>
                     <UniFormsReportTemplateWizard />
                   </React.Suspense>
                 } />
          <Route path={ROUTES.UNI_FORMS_REPORT_TEMPLATES}
                 element={<UniFormsReportTemplates />} />
          {/* "Get responses" screens.  Declared AFTER the static list
              paths above so ``/uni-forms/forms`` still resolves to the
              list — these only match the longer
              ``/uni-forms/forms/:formId/responses`` shape.  The module
              permission is all that's gated here; whether this user may
              read THESE submissions is decided by the server
              (``can_view_responses``) and the page renders a "no access"
              panel on a 403 rather than an empty table. */}
          <Route path={ROUTES.UNI_FORMS_FORM_RESPONSES()}
                 element={<UniFormsFormResponses />} />
          {/* The question builder.  Same reasoning as the responses
              routes above: declared after the static list paths so
              ``/uni-forms/forms`` keeps resolving to the list, and the
              module permission is all that's gated here — whether this
              user may *change* this form's questions is decided by the
              server (``can_manage``), and the page renders read-only
              with an explanation when the answer is no. */}
          <Route path={ROUTES.UNI_FORMS_FORM_QUESTIONS()}
                 element={<UniFormsQuestions />} />
          <Route path={ROUTES.UNI_FORMS_QUIZ_RESPONSES()}
                 element={<UniFormsQuizResponses />} />
          <Route path={ROUTES.UNI_FORMS_POLL_RESULTS()}
                 element={<UniFormsPollResults />} />
        </Route>

        <Route element={<RequirePermission permission="waba-chat" />}>
          <Route path={ROUTES.WABA_HOME} element={<WabaDashboard />} />
          <Route path={ROUTES.WABA_THREAD} element={<WabaInbox />} />
          <Route path={ROUTES.WABA_INBOX} element={<WabaInbox />} />
          <Route path={ROUTES.WABA_CONTACTS} element={<WabaContacts />} />
          <Route path={ROUTES.WABA_LISTS} element={<WabaLists />} />
          <Route path={ROUTES.WABA_TAGS} element={<WabaTags />} />
          <Route path={ROUTES.WABA_LABELS} element={<WabaLabels />} />
          <Route path={ROUTES.WABA_BROADCASTS} element={<WabaBroadcasts />} />
          <Route path={ROUTES.WABA_TEMPLATES} element={<WabaTemplates />} />
          <Route path={ROUTES.WABA_MEDIA} element={<WabaMedia />} />
          <Route path={ROUTES.WABA_ANALYTICS} element={<WabaAnalytics />} />
          <Route path={ROUTES.WABA_WEBHOOKS} element={<WabaWebhooks />} />
          <Route path={ROUTES.WABA_INCOMING_LOGS} element={<WabaIncomingLogs />} />
          <Route path={ROUTES.WABA_SETTINGS} element={<WabaSettings />} />
          <Route path={ROUTES.WABA_NUMBERS} element={<WabaNumbers />} />
          <Route path={ROUTES.WABA_S3} element={<WabaS3 />} />
          <Route path={ROUTES.WABA_RETRY} element={<WabaRetry />} />
          <Route path={ROUTES.WABA_THROTTLE} element={<WabaThrottle />} />
          <Route path={ROUTES.WABA_FAILED} element={<WabaFailed />} />
          <Route path={ROUTES.WABA_AUTOMATION} element={<WabaAutomation />} />
          <Route path={ROUTES.WABA_FLOW_BUILDER} element={<WabaFlowBuilder />} />
          <Route path={ROUTES.WABA_FLOWS} element={<WabaFlows />} />
          <Route path={ROUTES.WABA_API_KEYS} element={<WabaApiKeys />} />
          <Route path={ROUTES.WABA_QUICK_REPLIES} element={<WabaQuickReplies />} />
          <Route path={ROUTES.WABA_AUTO_REPLIES} element={<WabaAutoReplies />} />
          <Route path={ROUTES.WABA_TEAMS} element={<WabaTeams />} />
          <Route path={ROUTES.WABA_FORM_DIALOGS} element={<WabaFormDialogs />} />
          <Route path={ROUTES.WABA_FORM_SESSIONS} element={<WabaFormSessions />} />
          <Route path={ROUTES.WABA_KYC} element={<WabaKycSessions />} />
          <Route path={ROUTES.WABA_NOTES} element={<WabaNotes />} />
          <Route path={ROUTES.WABA_HEALTH} element={<WabaHealth />} />
          <Route path={ROUTES.WABA_HOOKS} element={<WabaHooks />} />
          <Route path={ROUTES.WABA_HELP} element={<WabaHelp />} />
          <Route path={ROUTES.WABA_FEATURES} element={<WabaFeatures />} />
        </Route>

        <Route element={<RequirePermission permission="rag" />}>
          <Route path={ROUTES.RAG_HOME} element={<RagDashboard />} />
          <Route path={ROUTES.RAG_KNOWLEDGE} element={<RagKnowledge />} />
          <Route path={ROUTES.RAG_CATEGORIES} element={<RagCategories />} />
          <Route path={ROUTES.RAG_VERSIONS} element={<RagVersions />} />
          <Route path={ROUTES.RAG_CHUNKS} element={<RagChunks />} />
          <Route path={ROUTES.RAG_TRAINING} element={<RagTraining />} />
          <Route path={ROUTES.RAG_QUALITY} element={<RagQuality />} />
          <Route path={ROUTES.RAG_FAQ} element={<RagFaq />} />
          <Route path={ROUTES.RAG_CHATS} element={<RagChats />} />
          <Route path={ROUTES.RAG_CONVERSATIONS} element={<RagConversations />} />
          <Route path={ROUTES.RAG_CUSTOMERS} element={<RagCustomers />} />
          <Route path={ROUTES.RAG_SALES} element={<RagSales />} />
          <Route path={ROUTES.RAG_ESCALATION} element={<RagEscalation />} />
          <Route path={ROUTES.RAG_FOLLOWUPS} element={<RagFollowups />} />
          <Route path={ROUTES.RAG_SEQUENCES} element={<RagSequences />} />
          <Route path={ROUTES.RAG_SEQUENCE_STEPS} element={<RagSequenceSteps />} />
          <Route path={ROUTES.RAG_TRACKERS} element={<RagTrackers />} />
          <Route path={ROUTES.RAG_REPORTS} element={<RagReports />} />
          <Route path={ROUTES.RAG_HEALTH_REPORTS} element={<RagHealthReports />} />
          <Route path={ROUTES.RAG_PRODUCTS} element={<RagProducts />} />
          <Route path={ROUTES.RAG_PLAYGROUND} element={<RagPlayground />} />
          <Route path={ROUTES.RAG_TEST_CHAT} element={<RagTestChat />} />
          <Route path={ROUTES.RAG_SYSTEMS} element={<RagSystems />} />
          <Route path={ROUTES.RAG_CONFIG} element={<RagConfig />} />
          <Route path={ROUTES.RAG_CONFIG_KEYS} element={<RagConfigKeys />} />
          <Route path={ROUTES.RAG_COSTS} element={<RagCosts />} />
          <Route path={ROUTES.RAG_AUDIT} element={<RagAudit />} />
          <Route path={ROUTES.RAG_ALERTS} element={<RagAlerts />} />
          <Route path={ROUTES.RAG_COMPLIANCE} element={<RagCompliance />} />
          <Route path={ROUTES.RAG_RESP_TPL} element={<RagResponseTemplates />} />
          <Route path={ROUTES.RAG_DOC_TPL} element={<RagDocumentTemplates />} />
          <Route path={ROUTES.RAG_FEEDBACK} element={<RagFeedback />} />
          <Route path={ROUTES.RAG_NOTES} element={<RagNotes />} />
          <Route path={ROUTES.RAG_SHOPIFY} element={<RagShopify />} />
          <Route path={ROUTES.RAG_TEAM} element={<RagTeam />} />
          <Route path={ROUTES.RAG_HOOKS} element={<RagHooks />} />
          <Route path={ROUTES.RAG_HELP} element={<RagHelp />} />
          <Route path={ROUTES.RAG_FEATURES} element={<RagFeatures />} />
          <Route path={ROUTES.RAG_AUTOMATION} element={<RagAutomation />} />
        </Route>

        <Route element={<RequirePermission permission="signalbridge" />}>
          <Route path={ROUTES.SIGNALBRIDGE_HOME} element={<SignalBridgeDashboard />} />
          <Route path={ROUTES.SIGNALBRIDGE_PIXELS} element={<SignalBridgePixels />} />
          <Route path={ROUTES.SIGNALBRIDGE_STORES} element={<SignalBridgeStores />} />
          <Route path={ROUTES.SIGNALBRIDGE_PARAMETERS} element={<SignalBridgeParameters />} />
          <Route path={ROUTES.SIGNALBRIDGE_TEST} element={<SignalBridgeTestEvent />} />
          <Route path={ROUTES.SIGNALBRIDGE_RESEND} element={<SignalBridgeResend />} />
          <Route path={ROUTES.SIGNALBRIDGE_UPLOAD} element={<SignalBridgeUpload />} />
          <Route path={ROUTES.SIGNALBRIDGE_BATCH} element={<SignalBridgeBatchDetail />} />
          <Route path={ROUTES.SIGNALBRIDGE_EVENT} element={<SignalBridgeEventDetail />} />
          <Route path={ROUTES.SIGNALBRIDGE_EVENTS} element={<SignalBridgeEvents />} />
          <Route path={ROUTES.SIGNALBRIDGE_TEMPLATES} element={<SignalBridgeTemplates />} />
          <Route path={ROUTES.SIGNALBRIDGE_MIDDLEWARE} element={<Navigate to={ROUTES.SIGNALBRIDGE_TEMPLATES} replace />} />
          <Route path={ROUTES.SIGNALBRIDGE_PAYLOADS} element={<SignalBridgePayloads />} />
          <Route path={ROUTES.SIGNALBRIDGE_HEALTH} element={<SignalBridgeConnHealth />} />
          <Route path={ROUTES.SIGNALBRIDGE_HELP} element={<SignalBridgeHelp />} />
          <Route path={ROUTES.SIGNALBRIDGE_DESTINATIONS} element={<SignalBridgeDestinations />} />
          <Route path={ROUTES.SIGNALBRIDGE_ROUTES} element={<SignalBridgeRoutes />} />
          <Route path={ROUTES.SIGNALBRIDGE_COLLECT} element={<SignalBridgeCollect />} />
          <Route path={ROUTES.SIGNALBRIDGE_API_KEYS} element={<SignalBridgeApiKeys />} />
          <Route path={ROUTES.SIGNALBRIDGE_ALERTS} element={<SignalBridgeAlerts />} />
          <Route path={ROUTES.SIGNALBRIDGE_REPORTS} element={<SignalBridgeReports />} />
          <Route path={ROUTES.SIGNALBRIDGE_PURCHASE_REPORT} element={<SignalBridgeBuyerReport />} />
          <Route path={ROUTES.SIGNALBRIDGE_AUDIT} element={<SignalBridgeAudit />} />
          <Route path={ROUTES.SIGNALBRIDGE_ONBOARDING} element={<SignalBridgeOnboarding />} />
          <Route path={ROUTES.SIGNALBRIDGE_SETTINGS} element={<SignalBridgeSettings />} />
          <Route path={ROUTES.SIGNALBRIDGE_PIXEL_GROUPS} element={<SignalBridgePixelGroups />} />
          <Route path={ROUTES.SIGNALBRIDGE_GOOGLE} element={<SignalBridgeGoogleWizard />} />
          <Route path={ROUTES.SIGNALBRIDGE_CONNECT_PIXEL} element={<SignalBridgePixelWizard />} />
          <Route path={ROUTES.SIGNALBRIDGE_CONNECT_STORE} element={<SignalBridgeStoreWizard />} />
          <Route path={ROUTES.SIGNALBRIDGE_CONFIGURE_EVENTS} element={<SignalBridgeEventsWizard />} />
          <Route path={ROUTES.SIGNALBRIDGE_MERGE_SETUP} element={<SignalBridgeMergeWizard />} />
          <Route path={ROUTES.SIGNALBRIDGE_MERGE} element={<SignalBridgeMergeBrowser />} />
          <Route path={ROUTES.SIGNALBRIDGE_JOURNEY} element={<SignalBridgeJourney />} />
          <Route path={ROUTES.SIGNALBRIDGE_DLQ} element={<SignalBridgeDlq />} />
          <Route path={ROUTES.SIGNALBRIDGE_COMPARE} element={<SignalBridgeCompare />} />
        </Route>

        {/* ===== Super Admin (super-admin-only) ===== */}
        <Route element={<RequirePermission permission="super-admin" />}>

          <Route path={ROUTES.SUPERADMIN_HOME}          element={<SuperAdminHome />} />
          <Route path={ROUTES.SUPERADMIN_LOGIN_SECURITY} element={<LoginSecurityConfig />} />
          <Route path={ROUTES.SUPERADMIN_DB_VIEWER_ADD} element={<DatabaseViewer />} />
          <Route path={ROUTES.SUPERADMIN_DB_VIEWER_TABLE} element={<DatabaseViewer />} />
          <Route path={ROUTES.SUPERADMIN_DB_VIEWER_SCHEMA} element={<DatabaseViewer />} />
          <Route path={ROUTES.SUPERADMIN_DB_VIEWER}     element={<DatabaseViewer />} />
          <Route path={ROUTES.SUPERADMIN_MODULE_DEFAULTS} element={<ModuleDefaults />} />
          <Route path={ROUTES.SUPERADMIN_SUCCESSYATRA} element={<SuccessYatraMove />} />
          <Route path={ROUTES.SUPERADMIN_ORG_MODULES}   element={<OrganisationModules />} />
          <Route path={ROUTES.SUPERADMIN_RESOURCE_NEW}  element={<SuperAdminResourceCreate />} />
          <Route path={ROUTES.SUPERADMIN_RESOURCE_EDIT} element={<SuperAdminResourceEdit />} />
          <Route path={ROUTES.SUPERADMIN_RESOURCE}      element={<SuperAdminResourceList />} />
        </Route>

        {/* Catch-all INSIDE the authenticated shell — render the
            "page not found" variant of AccessDenied so the user lands
            on a real, navigable page instead of a 404 / blank screen. */}
        <Route path="*" element={<AccessDenied reason="not-found" />} />
      </Route>

      {/* Anything that didn't match even the auth-shell route (very
          rare — basically only when AppShell itself fails to mount).
          Render the same friendly page rather than nothing. */}
      <Route path="*" element={<AccessDenied reason="not-found" />} />
    </Routes>
  );
}
